import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppModal, Button } from "@/components/ui";
import toast from "react-hot-toast";
import { automationError } from "@/components/features/automations/automationError";
import { retrieveFileDuplication } from "@/lib/data/requests/files/file-duplication";
import { fileDuplicationQueryOptions } from "@/lib/data/react-query/files/file-duplication";
import { invalidateFileQueries } from "@/lib/data/react-query/files/canonical-files";
import { libraryQueryKey } from "@/lib/data/react-query/file-library";
import {
  isFileDuplicationFinished,
  type FileDuplication,
} from "@/lib/data/schemas/file-duplication";
import type { TrackedOperation } from "./FileDuplicationProvider";

export function TrackedDuplication({
  operation,
  onDismiss,
}: {
  operation: TrackedOperation;
  onDismiss: () => void;
}) {
  const client = useQueryClient();
  const dismissRef = useRef(onDismiss);
  const notificationRef = useRef("");
  const succeededRef = useRef(false);
  const toastId = `file-duplication-${operation.id}`;
  useEffect(() => {
    dismissRef.current = onDismiss;
  }, [onDismiss]);
  useEffect(
    () => () => {
      notificationRef.current = "";
      if (!succeededRef.current) toast.dismiss(toastId);
    },
    [toastId],
  );
  const query = useQuery(fileDuplicationQueryOptions(operation.id));
  const [showFailures, setShowFailures] = useState(false);
  const [failureDetails, setFailureDetails] = useState<FileDuplication | null>(
    null,
  );
  const [loadingFailures, setLoadingFailures] = useState(false);
  const [failureError, setFailureError] = useState<string | null>(null);
  const result = query.data;
  const finished = result && isFileDuplicationFinished(result.status);
  useEffect(() => {
    if (!finished) return;
    void Promise.all([
      invalidateFileQueries(client),
      client.invalidateQueries({ queryKey: libraryQueryKey }),
    ]);
  }, [client, finished]);
  const loadFailures = useCallback(async () => {
    setShowFailures(true);
    setLoadingFailures(true);
    setFailureError(null);
    try {
      const items: FileDuplication["items"] = [];
      let cursor: string | undefined;
      const seenCursors = new Set<string>();
      do {
        const page = await retrieveFileDuplication(operation.id, cursor);
        items.push(...page.items);
        cursor = page.nextCursor ?? undefined;
        if (cursor && seenCursors.has(cursor))
          throw new Error("Unable to finish loading failures. Try again.");
        if (cursor) seenCursors.add(cursor);
        if (!cursor) {
          setFailureDetails({ ...page, items });
          break;
        }
      } while (cursor);
    } catch (error) {
      setFailureError(automationError(error));
    } finally {
      setLoadingFailures(false);
    }
  }, [operation.id]);
  const { refetch } = query;
  const queryError = query.error ? automationError(query.error) : null;
  const status = result?.status;
  const succeeded = result?.succeeded ?? 0;
  const failed = result?.failed ?? 0;
  const total = result?.total ?? 0;
  const errorMessage = result?.errorMessage;
  useEffect(() => {
    const notificationKey = JSON.stringify([
      queryError,
      status,
      succeeded,
      failed,
      total,
      errorMessage,
    ]);
    if (notificationRef.current === notificationKey) return;
    notificationRef.current = notificationKey;
    const options = { id: toastId, duration: undefined };
    if (queryError) {
      toast.error(
        <span>
          Unable to check duplication for {operation.label}. {queryError}{" "}
          <button className="underline" onClick={() => void refetch()}>
            Retry
          </button>
        </span>,
        { ...options, duration: Infinity },
      );
    } else if (status === "succeeded") {
      succeededRef.current = true;
      toast.success(`${operation.label}: ${succeeded} copied`, options);
      dismissRef.current();
    } else if (status === "failed" || status === "partial") {
      toast.error(
        <span>
          {operation.label}: {succeeded} copied · {failed} failed.{" "}
          {errorMessage}{" "}
          <button className="underline" onClick={() => void loadFailures()}>
            View failures
          </button>{" "}
          <button
            className="underline"
            onClick={() => {
              dismissRef.current();
            }}
          >
            Dismiss
          </button>
        </span>,
        { ...options, duration: Infinity },
      );
    } else {
      toast.loading(
        `${operation.label}: ${status === "running" ? `${succeeded + failed} of ${total} processed` : "Preparing duplication…"}`,
        options,
      );
    }
  }, [
    queryError,
    status,
    succeeded,
    failed,
    total,
    errorMessage,
    toastId,
    operation.label,
    refetch,
    loadFailures,
  ]);
  return (
    <AppModal
      open={showFailures}
      onOpenChange={setShowFailures}
      title="File duplication failures"
      description={`Files that could not be copied for ${operation.label}.`}
      size="xl"
      footer={
        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={() => {
              setShowFailures(false);
            }}
          >
            Close
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="font-medium break-words">{operation.label}</p>
        {errorMessage && <p role="alert">{errorMessage}</p>}
        {loadingFailures ? (
          <p role="status" className="text-muted-foreground">
            Loading failures…
          </p>
        ) : failureError ? (
          <div role="alert" className="space-y-2">
            <p className="text-destructive">{failureError}</p>
            <Button variant="outline" onClick={() => void loadFailures()}>
              Retry
            </Button>
          </div>
        ) : (
          failureDetails?.items
            .filter((item) => item.status === "failed")
            .map((item) => (
              <p key={item.sourceId} className="break-words">
                <span className="font-medium">
                  {item.sourceName ?? "Unavailable file"}:
                </span>{" "}
                {item.errorMessage ?? item.errorCode ?? "Could not copy"}
              </p>
            ))
        )}
      </div>
    </AppModal>
  );
}
